import {useCallback, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {
  useCreateRegistryMembersMutation,
  useDeleteRegistryMembersMutation,
  useUpdateTeamRegistryRoleMutation,
  useUpdateUserRegistryRoleMutation,
} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  getErrMsgFromError,
  propagateErrorsContext,
} from '../../../../util/errors';
import {AccessOption} from '../../../../util/permissions';
import {checkRegistryProject, globalRegistryType} from '../../common/utils';
import {PROJECT_READONLY_QUERY} from '../../graphql/readOnly.query';
import {REGISTRY_PROJECT_MEMBERS_QUERY} from '../../graphql/registryProjectMembers.query';
import {RegistryMember} from './types';

export function useAddRegistryMembers(
  entityName: string,
  registryProjectName: string
) {
  const [createRegistryMembers] = useCreateRegistryMembersMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: REGISTRY_PROJECT_MEMBERS_QUERY,
        variables: {entityName, projectName: registryProjectName},
      },
      {
        query: PROJECT_READONLY_QUERY,
        variables: {entityName, projectName: registryProjectName},
      },
    ],
  });
  const [isLoading, setIsLoading] = useState(false);
  async function addRegistryMembers(
    registryProjectId: string,
    usersToAdd: string[],
    teamsToAdd: string[],
    hideToast?: boolean
  ) {
    setIsLoading(true);
    try {
      const noMembersToAdd = usersToAdd.length === 0 && teamsToAdd.length === 0;
      if (!noMembersToAdd && registryProjectId !== '') {
        await createRegistryMembers({
          variables: {
            projectId: registryProjectId,
            userIds: usersToAdd,
            teamIds: teamsToAdd,
          },
        });
      }

      if (!hideToast) {
        toast('Updated members!');
      }
      setIsLoading(false);
      return;
    } catch (error) {
      console.error(
        'Error trying to add registry members: ',
        getErrMsgFromError(error)
      );
      if (!hideToast) {
        toast(
          `Error trying to add registry members: ${getErrMsgFromError(error)}`
        );
      }
      setIsLoading(false);
      return;
    }
  }
  return {addRegistryMembers, isLoading};
}

export function useDeleteRegistryMembers(
  entityName: string,
  registryProjectName: string
) {
  const [deleteRegistryMembers] = useDeleteRegistryMembersMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: REGISTRY_PROJECT_MEMBERS_QUERY,
        variables: {entityName, projectName: registryProjectName},
      },
      {
        query: PROJECT_READONLY_QUERY,
        variables: {entityName, projectName: registryProjectName},
      },
    ],
  });
  const [isLoading, setIsLoading] = useState(false);
  async function removeRegistryMembers(
    registryProjectId: string,
    usersToRemove: string[],
    teamsToRemove: string[]
  ) {
    setIsLoading(true);
    try {
      const noMembersToRemove =
        usersToRemove.length === 0 && teamsToRemove.length === 0;
      if (!noMembersToRemove && registryProjectId !== '') {
        await deleteRegistryMembers({
          variables: {
            projectId: registryProjectId,
            userIds: usersToRemove,
            teamIds: teamsToRemove,
          },
        });
      }

      toast('Updated members!');
      setIsLoading(false);
      return;
    } catch (error) {
      console.error(
        'Error trying to delete registry members: ',
        extractErrorMessageFromApolloError(error)
      );
      toast(
        `Error trying to delete members in the registry: ${getErrMsgFromError(
          error
        )}`
      );
      setIsLoading(false);
      return;
    }
  }
  return {removeRegistryMembers, isLoading};
}

export const useOnUserRegistryRoleChange = (
  entityName: string,
  registryProjectName: string
) => {
  const [updateUserRegistryRoleMutation] = useUpdateUserRegistryRoleMutation({
    refetchQueries: [
      {
        query: REGISTRY_PROJECT_MEMBERS_QUERY,
        variables: {
          entityName,
          projectName: registryProjectName,
        },
      },
    ],
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const onUserRegistryRoleChange = useCallback(
    async (userId: string, registryProjectId: string, role: string) => {
      try {
        setIsUpdating(true);
        if (userId !== '' && registryProjectId !== '' && role !== '') {
          await updateUserRegistryRoleMutation({
            variables: {
              userId,
              projectId: registryProjectId,
              userProjectRole: role,
            },
            context: propagateErrorsContext(),
          });
          const {registryName} = checkRegistryProject(registryProjectName);
          Analytics.track('Registry interaction', {
            action: 'update member role',
            entityName,
            registryProjectName,
            registryName,
          });
          toast.success('User role updated');
        }
      } catch (err) {
        toast.error(`Failed to update user role: ${getErrMsgFromError(err)}`);
      } finally {
        setIsUpdating(false);
      }
    },
    [updateUserRegistryRoleMutation, entityName, registryProjectName]
  );

  return {
    isUpdating,
    onUserRegistryRoleChange,
  };
};

export const useOnTeamRegistryRoleChange = (
  orgEntityName: string,
  registryProjectName: string
) => {
  const [updateTeamRegistryRoleMutation] = useUpdateTeamRegistryRoleMutation({
    refetchQueries: [
      {
        query: REGISTRY_PROJECT_MEMBERS_QUERY,
        variables: {
          entityName: orgEntityName,
          projectName: registryProjectName,
        },
      },
    ],
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const onTeamRegistryRoleChange = useCallback(
    async (teamId: string, registryProjectId: string, role: string) => {
      try {
        setIsUpdating(true);
        if (teamId !== '' && registryProjectId !== '' && role !== '') {
          await updateTeamRegistryRoleMutation({
            variables: {
              teamId,
              projectId: registryProjectId,
              teamProjectRole: role,
            },
            context: propagateErrorsContext(),
          });
          const {registryName} = checkRegistryProject(registryProjectName);
          Analytics.track('Registry interaction', {
            action: 'update team member role',
            entityName: orgEntityName,
            registryProjectName,
            registryName,
          });
          toast.success('Team role updated');
        }
      } catch (err) {
        toast.error(`Failed to update team role: ${getErrMsgFromError(err)}`);
      } finally {
        setIsUpdating(false);
      }
    },
    [updateTeamRegistryRoleMutation, orgEntityName, registryProjectName]
  );

  return {
    isUpdating,
    onTeamRegistryRoleChange,
  };
};

// Adds the registry owner (if they exist) to the project_members table
// If the registry owner isn't a part of the registry members list for a private(i.e. access = Organization) registry,
// this means that the registry was created before the change where we made the default inherited role as Viewer, in which
// case we'd expect the Owner to just inherit the Admin role.
export function useAddRegistryOwnerToMembersTable(
  registry: globalRegistryType,
  registryMembers: RegistryMember[],
  isRegistryMembersQueryLoading: boolean,
  isViewerAdminOrOwner: boolean
) {
  const {addRegistryMembers, isLoading: isAddRegistryMembersLoading} =
    useAddRegistryMembers(registry.entityName, registry.registryProjectName);

  useEffect(() => {
    if (
      registry.access === AccessOption.Private &&
      registry?.owner?.id != null &&
      !isRegistryMembersQueryLoading &&
      registryMembers.find(member => member.id === registry?.owner?.id) ==
        null &&
      isViewerAdminOrOwner
    ) {
      addRegistryMembers(
        registry.registryProjectID,
        [registry?.owner?.id],
        [],
        true
      ).catch(console.error);
    }
    // we should only trigger this when the registry object changes, so ignoring changes to the
    // addRegistryMember hook caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRegistryMembersQueryLoading,
    registryMembers,
    registry?.owner?.id,
    registry.access,
    registry.registryProjectID,
  ]);

  return {
    isLoading: isAddRegistryMembersLoading,
  };
}
